/**
 * @fileoverview AnimationService coordinates all game animations.
 * Handles success/failure animations, particle effects, and visual feedback.
 */

import Konva from 'konva';
import { CustomNode } from '../konva-extensions.js';
import { CONFIG } from '../core/GameConfig.js';
import { AnimationEffects } from '../utilities/Animations.js';
import { SparkleTrail } from '../utilities/SparkleTrail.js';

/**
 * Position coordinates for animations.
 */
export interface Position {
    x: number;
    y: number;
}

/**
 * AnimationService manages all visual animations in the game.
 * Provides a centralized API for success, failure, and particle effects.
 */
export class AnimationService {
    /**
     * Play success animation on a draggable shape.
     * Includes scale animation and star burst effect.
     * @param draggable - The shape to animate
     * @param onComplete - Optional callback when animation completes
     */
    playSuccessAnimation(draggable: CustomNode, onComplete?: () => void): void {
        // Create star burst animation
        const layer = draggable.getLayer();
        if (layer) {
            // Get color from the draggable
            const color = this.extractColor(draggable);
            AnimationEffects.createStarBurst(layer, draggable.x(), draggable.y(), color);
        }
        
        // Success animation - scale up then back to normal
        draggable.to({
            scaleX: CONFIG.SUCCESS_SCALE,
            scaleY: CONFIG.SUCCESS_SCALE,
            duration: CONFIG.SUCCESS_ANIMATION_DURATION,
            onFinish: () => {
                draggable.to({
                    scaleX: 1,
                    scaleY: 1,
                    duration: CONFIG.SUCCESS_ANIMATION_DURATION,
                    onFinish: () => {
                        if (onComplete) {
                            onComplete();
                        }
                    }
                });
            }
        });
    }
    
    /**
     * Play failure animation - bounce draggable back to original position.
     * @param draggable - The shape to animate
     * @param originalPos - The position to bounce back to
     * @param onComplete - Optional callback when animation completes
     */
    playFailureAnimation(draggable: CustomNode, originalPos: Position, onComplete?: () => void): void {
        draggable.to({
            x: originalPos.x,
            y: originalPos.y,
            duration: 0.3,
            easing: Konva.Easings.EaseOut,
            onFinish: () => {
                if (onComplete) {
                    onComplete();
                }
            }
        });
    }
    
    /**
     * Create a sparkle trail for a draggable node.
     * @param layer - The Konva layer to add sparkles to
     * @returns SparkleTrail instance
     */
    createSparkleTrail(layer: Konva.Layer): SparkleTrail {
        return new SparkleTrail(layer);
    }
    
    /**
     * Create a star burst effect at a specific location.
     * @param layer - The Konva layer to add the effect to
     * @param x - X coordinate
     * @param y - Y coordinate
     * @param color - Color of the stars
     */
    createStarBurst(layer: Konva.Layer, x: number, y: number, color: string): void {
        AnimationEffects.createStarBurst(layer, x, y, color);
    }
    
    /**
     * Animate target fade out after successful match.
     * @param target - The target shape to fade out
     * @param duration - Animation duration in seconds
     */
    fadeOutTarget(target: CustomNode, duration: number = CONFIG.SNAP_DURATION): void {
        target.to({
            opacity: 0,
            duration: duration
        });
    }
    
    /**
     * Show success message with animation on feedback text.
     * @param feedbackText - Konva.Text object for feedback
     * @param message - Message to display (randomly selected if not provided)
     */
    showSuccessMessage(feedbackText: Konva.Text, message?: string): void {
        // Show emoji overlay in DOM
        const successMsg = document.getElementById('successMessage');
        if (successMsg) {
            successMsg.style.opacity = '1';
            successMsg.style.transform = 'translate(-50%, -50%) scale(1)';
            
            setTimeout(() => {
                successMsg.style.transition = 'all 0.5s ease';
                successMsg.style.opacity = '0';
                successMsg.style.transform = 'translate(-50%, -50%) scale(0.5)';
            }, CONFIG.SUCCESS_MESSAGE_DURATION);
        }
        
        // Show feedback text on canvas
        const displayMessage = message || CONFIG.FEEDBACK_MESSAGES[
            Math.floor(Math.random() * CONFIG.FEEDBACK_MESSAGES.length)
        ];
        
        feedbackText.text(displayMessage);
        feedbackText.offsetX(feedbackText.width() / 2);
        feedbackText.y(CONFIG.FEEDBACK_Y_POSITION + 30);
        
        feedbackText.to({
            opacity: 1,
            y: CONFIG.FEEDBACK_Y_POSITION,
            duration: 0.3,
            easing: Konva.Easings.EaseOut,
            onFinish: () => {
                setTimeout(() => {
                    feedbackText.to({
                        opacity: 0,
                        y: CONFIG.FEEDBACK_Y_POSITION + 30,
                        duration: 0.3
                    });
                }, 1000);
            }
        });
    }
    
    /**
     * Extract color from a Konva shape or group.
     * @param node - Konva shape or group
     * @returns Color hex string
     */
    private extractColor(node: any): string {
        let color = '#FFFF00'; // Default yellow color
        
        // Check if color is stored directly on the node
        if (node.shapeColor) {
            return node.shapeColor.fill || node.shapeColor.stroke || color;
        }
        
        // Try to get fill color first
        if (typeof node.fill === 'function') {
            const fillColor = node.fill();
            if (fillColor && fillColor !== '') {
                color = fillColor;
            } else if (typeof node.stroke === 'function') {
                const strokeColor = node.stroke();
                if (strokeColor && strokeColor !== '') {
                    color = strokeColor;
                }
            }
        } else if (node.children && node.children.length > 0) {
            // For Groups, get color from first child shape
            const firstChild = node.children[0];
            if (typeof firstChild.fill === 'function') {
                const fillColor = firstChild.fill();
                if (fillColor && fillColor !== '') {
                    color = fillColor;
                } else if (typeof firstChild.stroke === 'function') {
                    const strokeColor = firstChild.stroke();
                    if (strokeColor && strokeColor !== '') {
                        color = strokeColor;
                    }
                }
            }
        }
        
        return color;
    }
}
